import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { fromSlug } from './slug';
import type { GlobalRole } from '../types';

/**
 * Page + role context passed to the Help chatbot so answers are tailored to
 * who the user is and where they are in the app.
 */
export interface HelpContext {
  role: GlobalRole;
  roleInOrg?: string;
  workspaceType: 'agency' | 'brand' | 'kol' | 'admin' | 'unknown';
  currentRoute: string;
  currentModule: string;
  entity: Record<string, string>;
}

const PORTAL_BY_PREFIX: Record<string, HelpContext['workspaceType']> = {
  agency: 'agency',
  brand: 'brand',
  kol: 'kol',
  admin: 'admin',
};

/** Map a pathname to a human-friendly module name for context. */
export function moduleFromPath(pathname: string): string {
  const segs = pathname.split('/').filter(Boolean);
  // segs[0] = portal, segs[1] = section
  const section = segs[1] || segs[0] || 'home';
  const pretty = section.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  return pretty;
}

/** Extract entity ids (campaign/kol/invitation/etc.) from the current path. */
function entityFromPath(pathname: string): Record<string, string> {
  const segs = pathname.split('/').filter(Boolean);
  const entity: Record<string, string> = {};
  const grab = (key: string, idx: number) => {
    const raw = segs[idx];
    if (raw && raw !== 'new' && raw !== 'edit') entity[key] = fromSlug(decodeURIComponent(raw));
  };
  // /:portal/jobs/:id, /:portal/kols/:id, /:portal/invitations/:id, etc.
  for (let i = 1; i < segs.length - 1; i++) {
    const collection = segs[i];
    const map: Record<string, string> = {
      jobs: 'campaignId',
      kols: 'kolId',
      invitations: 'invitationId',
      scripts: 'scriptId',
      submissions: 'submissionId',
      brands: 'brandId',
      agencies: 'agencyId',
    };
    if (map[collection]) grab(map[collection], i + 1);
  }
  // Also expose a generic `id` (first entity found) for convenience.
  const firstId = Object.values(entity)[0];
  if (firstId) entity.id = firstId;
  return entity;
}

export function useHelpContext(): HelpContext | null {
  const user = useAuthStore((s) => s.user);
  const location = useLocation();

  return useMemo(() => {
    if (!user) return null;
    const prefix = location.pathname.split('/').filter(Boolean)[0] ?? '';
    return {
      role: user.role,
      roleInOrg: user.roleInOrg,
      workspaceType: PORTAL_BY_PREFIX[prefix] ?? 'unknown',
      currentRoute: location.pathname,
      currentModule: moduleFromPath(location.pathname),
      entity: entityFromPath(location.pathname),
    };
  }, [user, location.pathname]);
}
